import { Input, Switch, Typography } from 'antd';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import DrawerSelect from 'src/components/DrawerSelect';
import { FieldDescriptionText, FormItem, TabsContentWrapper } from 'src/components/SharedDetailsComponents';
import { useGlobal } from 'src/contexts/GlobalContext';
import SettingCard from 'src/pages/Devices/DeviceDetails/TabPages/SensorSettings/components/SettingCard';

import { useSiteDetails } from '../../SiteDetailsContext';
import {
  HeaderWrapper,
  PrivacyPolicyWrapper,
  SelectWrapper,
  SpacerStyled,
  StyledDivider,
  StyledSelect,
} from './Design.styled';

export const Design = () => {
  const { t } = useTranslation();
  const { formMethods, site, clientAssets } = useSiteDetails();
  const { isDesktop } = useGlobal();
  const { watch } = formMethods;
  const enabled = watch('design.introduction.enabled');

  const LOGO_OPTIONS = clientAssets
    ?.filter((asset) => asset.id && asset.name)
    .map((asset) => ({
      label: `${asset.name}`,
      value: `${asset.id}`,
    }));

  return (
    <>
      <SettingCard
        title={t('site.design.introduction.title')}
        description={t('site.design.introduction.description')}
        items={[
          {
            header: (
              <HeaderWrapper>
                {t('site.design.introduction.landingPage')}
                <Controller
                  name="design.introduction.enabled"
                  control={formMethods.control}
                  render={({ field }) => (
                    <Switch
                      {...field}
                      defaultChecked={!!site?.design?.introduction?.enabled}
                      data-testid="introduction-enabled-switch"
                    />
                  )}
                />
              </HeaderWrapper>
            ),
            isContentHidden: !enabled,
            content: (
              <TabsContentWrapper $isDesktop={false}>
                <FormItem
                  $isDesktop={false}
                  label={t('site.design.introduction.landingPageHtml')}
                  name="design.introduction.html"
                >
                  <Controller
                    name="design.introduction.html"
                    control={formMethods.control}
                    render={({ field }) => (
                      <Input.TextArea
                        {...field}
                        placeholder={t('site.design.introduction.landingPageHtmlPlaceholder')}
                        data-testid="introduction-html-textarea"
                      />
                    )}
                  />
                </FormItem>
              </TabsContentWrapper>
            ),
          },
          {
            header: t('site.design.introduction.campaignPage.title'),
            content: (
              <TabsContentWrapper $isDesktop={false}>
                <FormItem
                  $isDesktop={false}
                  label={t('site.design.introduction.campaignPage.pageTitle')}
                  name="design.campaignPageTitle"
                >
                  <Controller
                    name="design.campaignPageTitle"
                    control={formMethods.control}
                    render={({ field }) => (
                      <Input placeholder={t('site.design.introduction.campaignPage.pageTitle')} {...field} />
                    )}
                  />
                </FormItem>
                <FormItem
                  $isDesktop={false}
                  label={t('site.design.introduction.campaignPage.campaignsPageDescriptionTitle')}
                  name="design.campaignPageDescriptionHtml"
                >
                  <Controller
                    name="design.campaignPageDescriptionHtml"
                    control={formMethods.control}
                    render={({ field }) => (
                      <Input.TextArea
                        placeholder={t('site.design.introduction.campaignPage.campaignsPageDescriptionTitle')}
                        {...field}
                        data-testid="campaign-description-textarea"
                      />
                    )}
                  />
                </FormItem>
              </TabsContentWrapper>
            ),
          },
        ]}
      />
      {isDesktop ? <StyledDivider /> : <SpacerStyled data-testid="spacer" />}
      <SettingCard
        title={t('site.design.imagery.title')}
        description={t('site.design.imagery.description')}
        items={[
          {
            content: (
              <TabsContentWrapper $isDesktop={isDesktop}>
                <FormItem
                  $isDesktop={isDesktop}
                  label={t('site.design.imagery.mainLogo')}
                  name="design.largeLogoImageId"
                >
                  <Controller
                    name="design.largeLogoImageId"
                    control={formMethods.control}
                    render={({ field }) => {
                      if (isDesktop) {
                        return (
                          <SelectWrapper>
                            <StyledSelect
                              {...field}
                              onChange={(value) => field.onChange(value ?? null)}
                              maxTagCount="responsive"
                              placeholder={t('site.design.imagery.selectImage')}
                              options={LOGO_OPTIONS ?? []}
                              data-testid="main-logo-select"
                              allowClear
                            />
                          </SelectWrapper>
                        );
                      } else {
                        return (
                          <DrawerSelect
                            {...field}
                            options={LOGO_OPTIONS ?? []}
                            onOk={(value) => field.onChange(value || null)}
                            title={t('site.design.imagery.mainLogo')}
                            placeholder={t('site.design.imagery.selectImage')}
                            onOkText={t('site.design.imagery.saveImage')}
                            size="default"
                            data-testid="main-logo-drawer-select"
                          />
                        );
                      }
                    }}
                  />
                </FormItem>
                <FormItem $isDesktop={isDesktop} label={t('site.design.imagery.icon')} name="design.smallLogoImageId">
                  <Controller
                    name="design.smallLogoImageId"
                    control={formMethods.control}
                    render={({ field }) => {
                      if (isDesktop) {
                        return (
                          <SelectWrapper>
                            <StyledSelect
                              {...field}
                              onChange={(value) => field.onChange(value ?? null)}
                              maxTagCount="responsive"
                              placeholder={t('site.design.imagery.selectImage')}
                              options={LOGO_OPTIONS ?? []}
                              data-testid="icon-select"
                              allowClear
                            />
                          </SelectWrapper>
                        );
                      } else {
                        return (
                          <DrawerSelect
                            {...field}
                            options={LOGO_OPTIONS ?? []}
                            onOk={(value) => field.onChange(value || null)}
                            title={t('site.design.imagery.icon')}
                            placeholder={t('site.design.imagery.selectImage')}
                            onOkText={t('site.design.imagery.saveIcon')}
                            size="default"
                            data-testid="icon-drawer-select"
                          />
                        );
                      }
                    }}
                  />
                </FormItem>
                <FormItem
                  $isDesktop={isDesktop}
                  label={t('site.design.imagery.backgroundImage')}
                  name="design.backgroundImageId"
                >
                  <Controller
                    name="design.backgroundImageId"
                    control={formMethods.control}
                    render={({ field }) => {
                      if (isDesktop) {
                        return (
                          <SelectWrapper>
                            <StyledSelect
                              {...field}
                              onChange={(value) => field.onChange(value ?? null)}
                              maxTagCount="responsive"
                              placeholder={t('site.design.imagery.selectImage')}
                              options={LOGO_OPTIONS ?? []}
                              data-testid="background-image-select"
                              allowClear
                            />
                          </SelectWrapper>
                        );
                      } else {
                        return (
                          <DrawerSelect
                            {...field}
                            options={LOGO_OPTIONS ?? []}
                            onOk={(value) => field.onChange(value || null)}
                            title={t('site.design.imagery.backgroundImage')}
                            placeholder={t('site.design.imagery.selectImage')}
                            onOkText={t('site.design.imagery.saveBackgroundImage')}
                            size="default"
                            data-testid="background-image-drawer-select"
                          />
                        );
                      }
                    }}
                  />
                </FormItem>
              </TabsContentWrapper>
            ),
          },
        ]}
      />
      {isDesktop ? <StyledDivider /> : <SpacerStyled data-testid="spacer" />}
      <SettingCard
        title={t('site.design.customContent.title')}
        description={t('site.design.customContent.description')}
        items={[
          {
            header: t('site.design.customContent.privacyPolicy'),
            content: (
              <TabsContentWrapper $isDesktop={false}>
                <FormItem
                  $isDesktop={false}
                  label={t('site.design.customContent.privacyPolicyFieldLabel')}
                  name="design.privacyPolicyHtml"
                >
                  <Controller
                    name="design.privacyPolicyHtml"
                    control={formMethods.control}
                    render={({ field }) => (
                      <Input.TextArea
                        placeholder={t('site.design.customContent.customFooterHTMLPlaceholder')}
                        {...field}
                        data-testid="privacy-policy-textarea"
                      />
                    )}
                  />
                </FormItem>
              </TabsContentWrapper>
            ),
          },
          {
            content: (
              <TabsContentWrapper $isDesktop={false}>
                <FormItem
                  $isDesktop={false}
                  label={t('site.design.customContent.privacyPolicyAgreementText')}
                  name="design.privacyPolicyAgreementText"
                >
                  <Controller
                    name="design.privacyPolicyAgreementText"
                    control={formMethods.control}
                    render={({ field }) => (
                      <PrivacyPolicyWrapper>
                        <Typography.Text>{t('site.design.customContent.privacyPolicyLabel')}</Typography.Text>
                        <FieldDescriptionText>
                          {`${t('site.design.customContent.example')}: ${t('site.design.customContent.privacyPolicyDescription')}`}
                        </FieldDescriptionText>
                        <Input
                          placeholder={t('site.design.customContent.privacyPolicyDescription')}
                          {...field}
                          data-testid="privacy-policy-textarea"
                        />
                      </PrivacyPolicyWrapper>
                    )}
                  />
                </FormItem>
              </TabsContentWrapper>
            ),
          },
          {
            header: t('site.design.customContent.footer'),
            content: (
              <TabsContentWrapper $isDesktop={false}>
                <FormItem
                  $isDesktop={false}
                  label={t('site.design.customContent.wicketFooter')}
                  name="design.useWicketFooter"
                >
                  <Controller
                    name="design.useWicketFooter"
                    control={formMethods.control}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        defaultChecked={!!site?.design?.useWicketFooter}
                        data-testid="use-wicket-footer-switch"
                      />
                    )}
                  />
                </FormItem>
                <FormItem
                  $isDesktop={false}
                  label={t('site.design.customContent.wicketFooterHTML')}
                  name="design.footerHtml"
                >
                  <Controller
                    name="design.footerHtml"
                    control={formMethods.control}
                    render={({ field }) => (
                      <Input.TextArea
                        placeholder={t('site.design.customContent.customFooterHTMLPlaceholder')}
                        {...field}
                        data-testid="footer-html-textarea"
                      />
                    )}
                  />
                </FormItem>
              </TabsContentWrapper>
            ),
          },
        ]}
      />
    </>
  );
};
